/** Actions ▾ — le même bouton et le même menu, liste et édition. */
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ActionChevron } from "../../components/ActionChevron";
import { Button } from "../../components/Button";
import { placeFixedMenu, watchMenuPlace } from "../../components/placeMenu";
import type { GatewayProvider } from "../../payments/gateways";
import { IcoCollect, IcoCopy, IcoCredit, IcoEdit, IcoEye, IcoInvoice, IcoMail, IcoPay, IcoPrint, IcoRepeat, IcoSeal, IcoTrash } from "./QuoteMenuIcons";

const MENU_W = 280;
const MENU_H = 560;

export type QuoteDocMenuProps = {
  label?: string;
  busy?: boolean;
  isDraft: boolean;
  converted?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onEdit?: () => void;
  onCloneQuote?: () => void;
  onCloneInvoice?: () => void;
  onCloneRecurring?: () => void;
  onSend?: () => void;
  onPdf?: () => void;
  onPrint?: () => void;
  onArchive?: () => void;
  onTrace?: () => void;
  onPay?: () => void;
  linked?: ReadonlySet<GatewayProvider>;
  onCollect?: () => void;
  onGcRefresh?: () => void;
  onConvert?: () => void;
  onCredit?: () => void;
  onOpenCredit?: () => void;
  onDelete?: () => void;
};

export function QuoteDocMenu({
  label = "Actions",
  busy,
  isDraft,
  converted = false,
  open: openProp,
  onOpenChange,
  onEdit,
  onCloneQuote,
  onCloneInvoice,
  onCloneRecurring,
  onSend,
  onPdf,
  onPrint,
  onArchive,
  onTrace,
  onPay,
  linked,
  onCollect,
  onGcRefresh,
  onConvert,
  onCredit,
  onOpenCredit,
  onDelete,
}: QuoteDocMenuProps) {
  const box = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [inner, setInner] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, maxHeight: 480 });
  const open = openProp ?? inner;

  function setOpen(next: boolean) {
    if (onOpenChange) onOpenChange(next);
    if (openProp === undefined) setInner(next);
  }

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = box.current?.querySelector("button")?.getBoundingClientRect();
      if (!r) return;
      setPos(placeFixedMenu(r, MENU_W, menu.current?.scrollHeight || MENU_H));
    };
    const stop = watchMenuPlace(place);
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (box.current?.contains(t) || menu.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => {
      stop();
      document.removeEventListener("mousedown", onDoc);
    };
  }, [open]);

  function pick(fn?: () => void) {
    return () => {
      setOpen(false);
      fn?.();
    };
  }

  function row(fn: (() => void) | undefined, ico: ReactNode, text: string, danger = false) {
    if (!fn) return null;
    return (
      <button
        type="button"
        className={["fx-quote-menu__item", danger ? "is-danger" : ""].filter(Boolean).join(" ")}
        role="menuitem"
        onClick={pick(fn)}
      >
        {ico} {text}
      </button>
    );
  }

  return (
    <div className="fx-quote-menu" ref={box}>
      <Button className="fx-chev-btn" disabled={busy} aria-expanded={open} onClick={() => setOpen(!open)}>
        {label} <ActionChevron />
      </Button>
      {open
        ? createPortal(
            <div
              ref={menu}
              className="fx-quote-menu__list"
              role="menu"
              style={{ top: pos.top, left: pos.left, width: MENU_W, maxHeight: pos.maxHeight, overflowY: "auto" }}
            >
              {row(onEdit, <IcoEdit />, isDraft ? "Éditer" : "Ouvrir")}
              {row(onPay, <IcoPay />, "Marquer comme payée")}
              {row(linked?.has("gocardless") ? onCollect : undefined, <IcoCollect />, "Prélever avec GoCardless")}
              {row(linked?.has("gocardless") ? onGcRefresh : undefined, <IcoCollect />, "Actualiser le prélèvement")}
              {row(onSend, <IcoMail />, isDraft ? "Envoyer par e-mail" : "Renvoyer par e-mail")}
              {row(onPdf, <IcoEye />, "Voir le PDF")}
              {row(onPrint, <IcoPrint />, "Télécharger / Imprimer le PDF")}
              {row(onArchive, <IcoSeal />, "Voir l’archive")}
              {row(onCloneQuote, <IcoCopy />, "Cloner en devis")}
              {row(onCloneInvoice, <IcoInvoice />, "Cloner en facture")}
              {row(onCloneRecurring, <IcoRepeat />, "Cloner en facture récurrente")}
              {!isDraft && !converted ? row(onConvert, <IcoInvoice />, "Convertir en facture") : null}
              {row(onCredit, <IcoCredit />, "Annuler / créer un avoir", true)}
              {row(onOpenCredit, <IcoCredit />, "Voir l’avoir")}
              {isDraft ? row(onDelete, <IcoTrash />, "Supprimer", true) : null}
              {onTrace ? <div className="fx-quote-menu__sep" role="separator" /> : null}
              {row(onTrace, <IcoSeal />, "Preuve d’intégrité")}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
